import type { FC, PropsWithChildren } from 'react'
import type {
	LocationResult,
	CoworkingSolution,
} from '@alexanderathoodly/data-models'
import { Helmet } from 'react-helmet-async'
import { getLocalBusinessObj } from '~/structured_data/localbusiness'
import type { OfferInfo } from '~/structured_data/localbusiness'
import { useQuery } from 'react-query'
import {
	getProviderQuery,
	getTestimonialsQuery,
	servicesQuery,
} from './queries'
import { SERVICES } from './services'
import { useSolutionDisplay, useMemberships } from './utils'
import { getFaqPage } from '~/structured_data/faqsection'
import { useFAQs } from './faqs'
import { useTranslation } from 'react-i18next'

export const CoworkinLocationHelmet: FC<
	PropsWithChildren<{
		location: LocationResult
	}>
> = ({ location, children }) => {
	const { i18n, t } = useTranslation()
	const { data: provider } = useQuery(getProviderQuery(location.provider))
	const { data: testimonials } = useQuery(getTestimonialsQuery(location.id))
	const { data: services } = useQuery(servicesQuery)
	const { cheapestSolution, tabsData } = useSolutionDisplay()
	const { data: memberships } = useMemberships(location.id)
	const FAQs = useFAQs()
	const isLoaded = !(
		provider == undefined ||
		testimonials == undefined ||
		services == undefined ||
		cheapestSolution == undefined ||
		memberships == undefined ||
		tabsData == undefined
	)

	const solutionList = tabsData
		? tabsData.reduce((acc, tab) => {
				return acc.concat(tab.solutions)
		  }, [] as CoworkingSolution[])
		: []
	const solutionOffersInfo: OfferInfo[] = solutionList.map((solution, i) => {
		return {
			type: solution.is_big_office ? 'bigOffice' : 'rooms',
			name: solution.is_big_office
				? `${t('Egen Avdelning')}`
				: `${t('Privat Kontor ({{solution_rooms_length}} rum)', {
						solution_rooms_length: solution.rooms.length,
						count: solution.rooms.length,
				  })}`,
			identifier: solution.is_big_office
				? `${t('Egen Avdelning')}-${i + 1}`
				: `${t('Privat Kontor ({{solution_rooms_length}} rum)', {
						solution_rooms_length: solution.rooms.length,
						count: solution.rooms.length,
				  })}-${i + 1}`,
			price: solution.price ? solution.price : undefined,
			size: solution.size,
			workspaces: solution.workspaces,
			image: solution.images?.[0],
		}
	})
	const membershipsOffersInfo: OfferInfo[] = memberships
		? memberships.map((membership) => {
				return {
					type: 'membership',
					name: membership.name,
					identifier: membership.name,
					price: membership.price ? membership.price : undefined,
				}
		  })
		: []
	return (
		<Helmet>
			{children}
			{isLoaded && getFaqPage(FAQs)}
			<script type="application/ld+json">
				{isLoaded
					? JSON.stringify(
							getLocalBusinessObj(
								location.name,
								provider.name,
								i18n.language != 'en'
									? location.short_description
									: location.short_description_en,
								location.address,
								cheapestSolution,
								location.url,
								location.highlights.map((highlight) => t(highlight)).toString(),
								location.images[0],
								services
									.filter((s) => location.services.indexOf(s.id) > -1)
									.map((s) => SERVICES[s.name]?.name || ''),
								testimonials.map((t) => {
									return { review: t.content, author: t.person_name }
								}),
								solutionOffersInfo.concat(membershipsOffersInfo)
							)
					  )
					: ''}
			</script>
		</Helmet>
	)
}
